import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';
import {
  AvailableReport,
  ReportFilters,
  ReportType,
} from '../../types/reports';

interface ReportsState {
  availableReports: AvailableReport[];
  currentReport: any | null;
  reportHistory: Array<{
    id: string;
    type: ReportType;
    name: string;
    generatedAt: Date;
    filters: ReportFilters;
  }>;
  loading: boolean;
  error: string | null;
  filters: ReportFilters;
}

const initialState: ReportsState = {
  availableReports: [],
  currentReport: null,
  reportHistory: [],
  loading: false,
  error: null,
  filters: {},
};

// Async thunks
export const fetchAvailableReports = createAsyncThunk(
  'reports/fetchAvailable',
  async () => {
    const response = await api.get('/reports/available');
    return response.data.data.reports;
  }
);

export const generatePortfolioSummaryReport = createAsyncThunk(
  'reports/generatePortfolioSummary',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.planningPeriodId) params.append('planningPeriodId', filters.planningPeriodId);
    if (filters.scenarioId) params.append('scenarioId', filters.scenarioId);
    
    const response = await api.get(`/reports/portfolio-summary?${params.toString()}`);
    return response.data.data;
  }
);

export const generateEpicStatusReport = createAsyncThunk(
  'reports/generateEpicStatus',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.planningPeriodId) params.append('planningPeriodId', filters.planningPeriodId);
    if (filters.scenarioId) params.append('scenarioId', filters.scenarioId);
    
    const response = await api.get(`/reports/epic-status?${params.toString()}`);
    return response.data.data;
  }
);

export const generateFeatureReadinessReport = createAsyncThunk(
  'reports/generateFeatureReadiness',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.planningPeriodId) params.append('planningPeriodId', filters.planningPeriodId);
    if (filters.scenarioId) params.append('scenarioId', filters.scenarioId);
    
    const response = await api.get(`/reports/feature-readiness?${params.toString()}`);
    return response.data.data;
  }
);

export const generateCapacityUtilizationReport = createAsyncThunk(
  'reports/generateCapacityUtilization',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.scenarioId) params.append('scenarioId', filters.scenarioId);
    
    const response = await api.get(`/reports/capacity-utilization?${params.toString()}`);
    return response.data.data;
  }
);

export const generateDependencyReport = createAsyncThunk(
  'reports/generateDependencies',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.planningPeriodId) params.append('planningPeriodId', filters.planningPeriodId);
    
    const response = await api.get(`/reports/dependencies?${params.toString()}`);
    return response.data.data;
  }
);

export const generateSuccessMetricsReport = createAsyncThunk(
  'reports/generateSuccessMetrics',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.planningPeriodId) params.append('planningPeriodId', filters.planningPeriodId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    
    const response = await api.get(`/reports/success-metrics?${params.toString()}`);
    return response.data.data;
  }
);

export const generateHistoricalTrendReport = createAsyncThunk(
  'reports/generateHistoricalTrends',
  async (filters: ReportFilters) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    
    const response = await api.get(`/reports/historical-trends?${params.toString()}`);
    return response.data.data;
  }
);

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ReportFilters>) => {
      state.filters = action.payload;
    },
    clearCurrentReport: (state) => {
      state.currentReport = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    addToHistory: (state, action: PayloadAction<{
      id: string;
      type: ReportType;
      name: string;
      generatedAt: Date;
      filters: ReportFilters;
    }>) => {
      state.reportHistory.unshift(action.payload);
      // Keep only last 20 reports in history
      if (state.reportHistory.length > 20) {
        state.reportHistory = state.reportHistory.slice(0, 20);
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch available reports
    builder
      .addCase(fetchAvailableReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableReports.fulfilled, (state, action) => {
        state.loading = false;
        state.availableReports = action.payload;
      })
      .addCase(fetchAvailableReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch available reports';
      });

    // Portfolio Summary Report
    builder
      .addCase(generatePortfolioSummaryReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generatePortfolioSummaryReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generatePortfolioSummaryReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate portfolio summary report';
      });

    // EPIC Status Report
    builder
      .addCase(generateEpicStatusReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateEpicStatusReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateEpicStatusReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate EPIC status report';
      });

    // Feature Readiness Report
    builder
      .addCase(generateFeatureReadinessReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateFeatureReadinessReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateFeatureReadinessReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate feature readiness report';
      });

    // Capacity Utilization Report
    builder
      .addCase(generateCapacityUtilizationReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateCapacityUtilizationReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateCapacityUtilizationReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate capacity utilization report';
      });

    // Dependency Report
    builder
      .addCase(generateDependencyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateDependencyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateDependencyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate dependency report';
      });

    // Success Metrics Report
    builder
      .addCase(generateSuccessMetricsReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateSuccessMetricsReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateSuccessMetricsReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate success metrics report';
      });

    // Historical Trend Report
    builder
      .addCase(generateHistoricalTrendReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateHistoricalTrendReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateHistoricalTrendReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate historical trend report';
      });
  },
});

export const { setFilters, clearCurrentReport, clearError, addToHistory } = reportsSlice.actions;
export default reportsSlice.reducer;